/**
 * WCAG contrast helpers for colours that arrive as data, where no design token can
 * guarantee a readable foreground and `darkenColor`'s fixed factor does not either.
 */

type Rgb = { b: number; g: number; r: number };

/** WCAG 1.4.3 minimum for text below 18.66px bold / 24px regular. */
export const AA_NORMAL_TEXT_RATIO = 4.5;

// Starts at the 30% `darkenColor` used to apply, so nothing comes out lighter than today.
const MIN_SHADE = 0.3;
const SHADE_STEP = 0.05;

const BLACK: Rgb = { b: 0, g: 0, r: 0 };
const WHITE: Rgb = { b: 255, g: 255, r: 255 };

function parseHex(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const value = Number.parseInt(match[1], 16);
  return { b: value & 255, g: (value >> 8) & 255, r: (value >> 16) & 255 };
}

function toHex({ b, g, r }: Rgb): string {
  return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

function channelLuminance(channel: number): number {
  const ratio = channel / 255;
  return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance({ b, g, r }: Rgb): number {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

function ratio(a: Rgb, b: Rgb): number {
  const luminanceA = relativeLuminance(a);
  const luminanceB = relativeLuminance(b);
  return (Math.max(luminanceA, luminanceB) + 0.05) / (Math.min(luminanceA, luminanceB) + 0.05);
}

/** Blends `rgb` towards black (0) or white (255) by `amount` (0–1). */
function shade(rgb: Rgb, amount: number, towards: number): Rgb {
  return {
    b: Math.round(rgb.b + (towards - rgb.b) * amount),
    g: Math.round(rgb.g + (towards - rgb.g) * amount),
    r: Math.round(rgb.r + (towards - rgb.r) * amount),
  };
}

/** Contrast ratio between two hex colours, or null if either cannot be parsed. */
export function getContrastRatio(colorA: string, colorB: string): number | null {
  const a = parseHex(colorA);
  const b = parseHex(colorB);
  if (!a || !b) return null;
  return ratio(a, b);
}

/**
 * Text colour that reaches `minRatio` against `backgroundColor`, shading it towards
 * black or white so it keeps its hue. Undefined if the background cannot be parsed.
 */
export function getReadableTextColor(backgroundColor?: string, minRatio: number = AA_NORMAL_TEXT_RATIO): string | undefined {
  const background = parseHex(backgroundColor ?? '');
  if (!background) return undefined;

  // Measured, not guessed: black and white tie at luminance ~0.179, not 0.5.
  const towards = ratio(BLACK, background) >= ratio(WHITE, background) ? 0 : 255;

  for (let amount = MIN_SHADE; amount < 1; amount += SHADE_STEP) {
    const candidate = shade(background, amount, towards);
    if (ratio(candidate, background) >= minRatio) return toHex(candidate);
  }

  return towards === 0 ? '#000000' : '#ffffff';
}
